import { httpClient } from "@marketplace/http";
import { captureException } from "@marketplace/shared/utils";
import { notifications } from "@marketplace/ui/notifications";
import { getDefaultStore } from "jotai";

import { accessTokenAtom, userDataAtom } from "@/modules/auth";
import { router } from "@/router";

import { PUBLIC_ENDPOINTS } from "./http-client-configs";

const defaultStore = getDefaultStore();

let abortController = new AbortController(); // Global AbortController (module-private)

export const getAbortSignal = () => abortController.signal;

const convertEndpointToRegex = (endpoint: string): RegExp => {
  const pattern = endpoint
    .replace(/:[a-zA-Z]+Id/g, "\\d+") // Replace ":countryId" or similar with "\d+"
    .replace(/:[a-zA-Z]+/g, "[a-zA-Z0-9_-]+"); // Replace ":slug" or similar with a general match
  return new RegExp(`${pattern}`);
};

const extractEndpoint = (url: string): string => {
  try {
    // Handle both full URLs and relative paths
    const urlPath = url.startsWith("http") ? new URL(url).pathname : url;

    // Remove leading slashes and API version prefix if exists
    const pathWithoutSlashes = urlPath.replace(/^\/+/, "");
    const pathParts = pathWithoutSlashes.split("/");

    // Check if path starts with 'api/v' followed by a number
    if (
      pathParts.length >= 2 &&
      pathParts[0] === "api" &&
      /^v\d+$/.test(pathParts[1])
    ) {
      // Remove 'api' and version parts
      return pathParts.slice(2).join("/");
    }

    return pathWithoutSlashes;
  } catch {
    // If URL parsing fails, just remove leading slashes
    return url.replace(/^\/+/, "");
  }
};

export const checkIsPublicEndpoint = (url: string): boolean => {
  return PUBLIC_ENDPOINTS.some((endpoint) => {
    const pattern = convertEndpointToRegex(endpoint);
    return pattern.test(url);
  });
};

export const getAccessToken = () => {
  return defaultStore.get(accessTokenAtom);
};

export const setAccessToken = (token: string) => {
  return defaultStore.set(accessTokenAtom, token);
};

export const clearSession = async () => {
  // Abort all ongoing requests
  abortController.abort();

  // Reset AbortController for future requests
  abortController = new AbortController();

  await defaultStore.set(accessTokenAtom, null);
  await defaultStore.set(userDataAtom, null);

  router.update({
    context: {
      auth: {
        isAuthenticated: false,
        userData: null,
        userMenus: []
      }
    }
  });
};

export const logout = async (showToast = false, redirectToLogin = true) => {
  await clearSession();

  if (showToast) {
    notifications.show({
      variant: "error",
      message: "Session expired, please login again"
    });
  }

  if (redirectToLogin) {
    router.navigate({
      to: "/auth/login",
      replace: true
    });
  }
};

export const retryRequest = async (
  request: Request,
  options: RequestInit,
  newToken: string
): Promise<Response> => {
  try {
    // Normalize URL: httpClient likely expects a relative API path.
    // When request.url is absolute, convert it to a relative endpoint and preserve query params.
    const normalizedUrl = (() => {
      const urlStr = request.url;
      if (urlStr.startsWith("http")) {
        try {
          const u = new URL(urlStr);
          const endpoint = extractEndpoint(u.pathname); // e.g., order/seller/count_order
          const qs = u.search || ""; // preserve query string if any
          return `${endpoint}${qs}`; // pass relative endpoint to httpClient
        } catch {
          // Fallback: if URL parsing fails, return original
          return urlStr;
        }
      }
      return urlStr;
    })();

    // Uses the base httpClient (without seller interceptors) intentionally:
    // - Using the extended client would re-trigger afterResponse hooks and cause
    //   infinite retry loops on persistent 401s
    const response = await httpClient(normalizedUrl, {
      ...options,
      headers: {
        ...(options.headers instanceof Headers
          ? Object.fromEntries((options.headers as Headers).entries())
          : (options.headers as Record<string, string>) || {}),
        Authorization: `Bearer ${newToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response;
  } catch (error) {
    captureException(error);
    throw error; // Re-throw to allow caller to handle the error
  }
};
